// v1.25.66 — Web Push (VAPID) helper. Runtime VAPID key fetch + stale-sub
// force-refresh. Adds `forceFresh` option that unconditionally
// unsubscribes the browser (regardless of key match) so a dead endpoint
// gets replaced by a truly new one — critical for breaking the "stale
// endpoint" loop on production.
import api from "@/lib/api";

const BUILD_TIME_KEY = (typeof process !== "undefined" && process.env && process.env.REACT_APP_VAPID_PUBLIC_KEY) || "";
let _cachedPublicKey = BUILD_TIME_KEY || "";

async function getVapidPublicKey() {
    if (_cachedPublicKey) return _cachedPublicKey;
    try {
        const r = await api.get("/push/public-key");
        const k = (r?.data?.publicKey || "").trim();
        if (k) _cachedPublicKey = k;
        return _cachedPublicKey;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[webPush] failed to fetch VAPID public key:", e);
        return "";
    }
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

export function isPushSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

export function pushPermission() {
    return isPushSupported() ? Notification.permission : "unsupported";
}

/**
 * Read the current browser's push endpoint (if any) without creating one.
 * Used so /push/test can target just THIS device instead of blasting
 * every sub the party ever registered.
 */
export async function getCurrentEndpoint() {
    if (!isPushSupported()) return "";
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        return sub ? sub.endpoint : "";
    } catch {
        return "";
    }
}

export async function ensurePushSubscription({ silent = false, forceFresh = false } = {}) {
    if (!isPushSupported()) return { ok: false, reason: "browser-unsupported" };
    if (Notification.permission === "denied") return { ok: false, reason: "denied" };

    const publicKey = await getVapidPublicKey();
    if (!publicKey) {
        // eslint-disable-next-line no-console
        console.warn("[webPush] VAPID public key empty — server misconfigured?");
        return { ok: false, reason: "server-not-configured" };
    }
    try {
        const reg = await navigator.serviceWorker.ready;
        if (!reg || !reg.pushManager) return { ok: false, reason: "no-push-manager" };
        let sub = await reg.pushManager.getSubscription();

        // Force-fresh: unconditionally drop the existing sub so we get a
        // brand-new endpoint from the push gateway. This is what breaks
        // the "stale endpoint" loop when the browser's cached sub is
        // silently dead but its applicationServerKey still matches ours.
        if (sub && forceFresh) {
            try {
                await sub.unsubscribe();
                // eslint-disable-next-line no-console
                console.info("[webPush] forceFresh: dropped existing sub");
            } catch { /* best-effort */ }
            sub = null;
        }

        // If existing sub was made with a different applicationServerKey
        // (e.g. after the .env fix changed the key), the browser refuses to
        // subscribe again. Force-unsubscribe stale before re-registering.
        if (sub) {
            try {
                const existingKey = sub.options && sub.options.applicationServerKey;
                if (existingKey) {
                    const currBuf = urlBase64ToUint8Array(publicKey);
                    const oldArr = new Uint8Array(existingKey);
                    const mismatch = oldArr.length !== currBuf.length ||
                        oldArr.some((b, i) => b !== currBuf[i]);
                    if (mismatch) {
                        // eslint-disable-next-line no-console
                        console.warn("[webPush] stale sub with different key — unsubscribing");
                        await sub.unsubscribe();
                        sub = null;
                    }
                }
            } catch { /* best-effort */ }
        }

        if (!sub) {
            if (Notification.permission !== "granted") {
                if (silent) return { ok: false, reason: "permission-default" };
                const perm = await Notification.requestPermission();
                if (perm !== "granted") return { ok: false, reason: perm };
            }
            try {
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey),
                });
            } catch (subErr) {
                // eslint-disable-next-line no-console
                console.warn("[webPush] subscribe threw:", subErr?.name, subErr?.message);
                try {
                    const stale = await reg.pushManager.getSubscription();
                    if (stale) await stale.unsubscribe();
                } catch { /* noop */ }
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey),
                });
            }
        }

        const payload = sub.toJSON();
        payload.userAgent = (navigator.userAgent || "").slice(0, 200);
        await api.post("/push/subscribe", payload);
        try { localStorage.setItem("micm_push_subscribed", "1"); } catch { /* noop */ }
        // eslint-disable-next-line no-console
        console.info("[webPush] subscription posted:", sub.endpoint.slice(0, 60) + "…");
        return { ok: true, reason: "subscribed", endpoint: sub.endpoint };
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[webPush] subscribe failed:", e?.name, e?.message, e);
        return { ok: false, reason: "subscribe-failed", error: `${e?.name || "Error"}: ${e?.message || String(e)}` };
    }
}

export async function unsubscribeFromPush() {
    if (!isPushSupported()) return false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            const endpoint = sub.endpoint;
            await sub.unsubscribe();
            try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* noop */ }
        }
        try { localStorage.removeItem("micm_push_subscribed"); } catch { /* noop */ }
        return true;
    } catch {
        return false;
    }
}

/**
 * v1.25.66 — Server-side wipe of every sub this user/party ever registered.
 * Recovery button for the "N stale endpoints" loop. After this the user
 * just re-enables notifications from their current browser for a clean sub.
 */
export async function resetAllPushSubs() {
    try {
        const { data } = await api.post("/push/reset", {});
        return { ok: true, removed: data?.removed || 0 };
    } catch (e) {
        return { ok: false, error: e?.response?.data?.detail || e.message };
    }
}

export async function sendTestPush() {
    // v1.25.66 — Sends the current browser's endpoint so the server tests
    // ONLY this device, not every legacy sub the party ever registered.
    try {
        const endpoint = await getCurrentEndpoint();
        const { data } = await api.post("/push/test", endpoint ? { endpoint } : {});
        return data;
    } catch (e) {
        return { sent: 0, subs: 0, stale: false, error: e?.response?.data?.detail || e.message };
    }
}
